import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  model,
  effect,
  inject,
  signal,
  OnInit,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { DateRangePickerComponent as LibDateRangePicker } from 'nebula-ui-kit';
import { catchError, of, tap, Subject, takeUntil } from 'rxjs';
import { AlertStore, TranslationService } from '@nebula-shared/data-access';
import { TreasuryDateRangePickerServices } from './treasury-date-range-picker.service';

@Component({
  standalone: true,
  imports: [LibDateRangePicker],
  selector: 'app-treasury-date-range-picker',
  templateUrl: './treasury-date-range-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreasuryDateRangePickerComponent extends AlertStore implements OnInit, OnDestroy {
  protected service = inject(TreasuryDateRangePickerServices);
  protected translation = inject(TranslationService);
  private cdr = inject(ChangeDetectorRef);

  private isLoadingParams = false;
  private loadParamsPromise: Promise<void> | null = null;
  private paramsLoaded = false;
  private destroy$ = new Subject<void>();
  private isUpdatingFromEffect = false;

  protected t(key: string): string {
    return this.translation.translate(key);
  }

  start = input<Date | null>(null);
  end = input<Date | null>(null);
  minDate = input<Date | null>(null);
  maxDate = input<Date | null>(null);

  startLabel = input<string>('Fecha Desde');
  endLabel = input<string>('Fecha Hasta');
  placeholder = input<string>('dd/MM/yyyy');

  startChange = output<Date | null>();
  endChange = output<Date | null>();
  rangeChange = output<{ start: Date | null; end: Date | null }>();

  startModel = model<Date | null>(null);
  endModel = model<Date | null>(null);

  systemDate = signal<Date | null>(null);
  errorVigencia = signal<string | null>(null);

  private fechasInicializadas = false;
  private fechasSeteadasDesdeInput = false;

  constructor() {
    super();

    effect(() => {
      const start = this.start();
      const end = this.end();

      if (!this.isUpdatingFromEffect) {
        if (start || end) {
          this.fechasSeteadasDesdeInput = true;
          this.startModel.set(start);
          this.endModel.set(end);
        }
      }
    });

    effect(() => {
      const systemDateNow = this.systemDate();

      // Solo inicializar si:
      // 1. No se han inicializado las fechas
      // 2. La fecha del sistema está disponible
      // 3. NO hay fechas desde input (o son null)
      // 4. No estamos actualizando desde effect
      if (
        !this.fechasInicializadas &&
        systemDateNow !== null &&
        !this.isUpdatingFromEffect &&
        !this.fechasSeteadasDesdeInput
      ) {
        this.isUpdatingFromEffect = true;

        // Solo usar fechas por defecto si no hay fechas en los inputs
        const currentStart = this.start();
        const currentEnd = this.end();

        if (!currentStart && !currentEnd) {
          const defaultStart = this.getDefaultStart();
          const defaultEnd = this.getDefaultEnd();

          this.startModel.set(defaultStart);
          this.endModel.set(defaultEnd);

          this.isValidRange(defaultStart, defaultEnd);

          this.rangeChange.emit({
            start: defaultStart,
            end: defaultEnd,
          });
        } else {
          //  Si hay fechas en inputs, usarlas
          this.startModel.set(currentStart);
          this.endModel.set(currentEnd);
        }

        this.fechasInicializadas = true;
        this.isUpdatingFromEffect = false;
        this.cdr.detectChanges();
      }
    });
  }

  ngOnInit(): void {
    this.loadSystemParamsOnce();
  }

  private loadSystemParamsOnce(): void {
    if (this.loadParamsPromise) {
      return;
    }

    if (this.paramsLoaded) {
      return;
    }

    if (this.isLoadingParams) {
      return;
    }

    this.loadParamsPromise = this.performLoadSystemParams();

    this.loadParamsPromise.finally(() => {
      this.loadParamsPromise = null;
    });
  }

  private performLoadSystemParams(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isLoadingParams = true;

      this.service
        .getSystemDate()
        .pipe(
          tap((result) => {
            if (!result || !result.fechaSistema) {
              const errorMsg = 'No se pudo obtener la fecha del sistema de tesorería';
              this.setError(errorMsg);
              this.systemDate.set(null);
              this.isLoadingParams = false;
              reject(new Error(errorMsg));
              return;
            }

            this.systemDate.set(this.parseDateFromString(result.fechaSistema));
            this.paramsLoaded = true;
            this.isLoadingParams = false;
            resolve();
          }),
          catchError((error) => {
            const errorMsg =
              error?.message || 'No se pudo obtener la fecha del sistema de tesorería';
            this.setError(errorMsg);
            this.systemDate.set(null);
            this.isLoadingParams = false;
            reject(error);
            return of(null);
          }),
          takeUntil(this.destroy$),
        )
        .subscribe();
    });
  }

  refreshSystemParams(): void {
    this.paramsLoaded = false;
    this.isLoadingParams = false;
    this.loadParamsPromise = null;
    this.fechasInicializadas = false;
    this.loadSystemParamsOnce();
  }

  isParamsLoaded(): boolean {
    return this.paramsLoaded;
  }

  resetParamsState(): void {
    this.paramsLoaded = false;
    this.isLoadingParams = false;
    this.loadParamsPromise = null;
    this.fechasInicializadas = false;
  }

  private isValidRange(start: Date | null, end: Date | null): boolean {
    if (!start || !end) return true;

    if (start > end) {
      this.errorVigencia.set('La fecha de inicio no puede ser mayor a la fecha de fin');
      this.setError('La fecha de inicio no puede ser mayor a la fecha de fin');
      return false;
    }

    this.errorVigencia.set(null);
    this.clearAlert();
    return true;
  }

  onStartChange(date: Date | null): void {
    if (this.isUpdatingFromEffect) return;

    if (date && !this.isValidRange(date, this.endModel())) {
      this.startModel.set(this.start());
      return;
    }
    this.startChange.emit(date);
    this.rangeChange.emit({ start: date, end: this.endModel() });
  }

  onEndChange(date: Date | null): void {
    if (this.isUpdatingFromEffect) return;

    if (date && !this.isValidRange(this.startModel(), date)) {
      this.endModel.set(this.end());
      return;
    }
    this.endChange.emit(date);
    this.rangeChange.emit({ start: this.startModel(), end: date });
  }

  onRangeChange(range: { start: Date | null; end: Date | null }): void {
    if (this.isUpdatingFromEffect) return;

    if (!this.isValidRange(range.start, range.end)) {
      this.startModel.set(this.start());
      this.endModel.set(this.end());
      return;
    }
    this.rangeChange.emit(range);
  }

  private parseDateFromString(dateStr: string): Date {
    const parts = dateStr.split('-');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }

  private getDefaultStart(): Date {
    const now = this.systemDate() ?? new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }

  private getDefaultEnd(): Date {
    const now = this.systemDate() ?? new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const day = now.getDate();
    return new Date(year, month, day);
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loadParamsPromise = null;
    this.isLoadingParams = false;
  }
}
